/**
 * Raíz de la aplicación.
 *
 * Coordina tres cosas y nada más: sesión, sincronización y qué mesa está
 * seleccionada. Toda la lógica del negocio vive en `datos/repositorio.ts` y
 * todos los datos en IndexedDB — este archivo solo conecta cables.
 *
 * Vale la pena notar lo que **no** hay acá: ni un `fetch`, ni un `loading`
 * bloqueante, ni manejo de errores de red en los flujos de venta. Tomar un
 * pedido nunca espera al servidor, así que no hay nada que esperar.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Rail } from './componentes/Rail';
import { PanelMesas } from './componentes/PanelMesas';
import { PanelOrden } from './componentes/PanelOrden';
import { PanelLateral } from './componentes/PanelLateral';
import { ModalProductos } from './componentes/ModalProductos';
import { ModalPago } from './componentes/ModalPago';
import { ModalDatosMesa } from './componentes/ModalDatosMesa';
import { ModalCuentas } from './componentes/ModalCuentas';
import { ModalPuestoNuevo } from './componentes/ModalPuestoNuevo';
import { Aviso } from './componentes/Aviso';
import { Login } from './componentes/Login';
import { AperturaCaja } from './componentes/AperturaCaja';
import { ModalCierreCaja } from './componentes/ModalCierreCaja';
import { ModalCatalogo } from './componentes/ModalCatalogo';

import { useSesion } from './estado/sesion';
import { useAhora, useCatalogo, useCobros, useMesas, usePedido, usePendientesSync, useResumenTurno } from './datos/vistas';
import * as repo from './datos/repositorio';
import { bajarTodo, bajarTurno, iniciarSync, turnoGuardado } from './datos/sync';
import { iconoPorNombre } from './lib/iconos';
import { dispositivoId } from './lib/uuid';
import { pad2 } from './lib/formato';
import type { PagoLocal } from './datos/db';
import type { MesaVista } from './datos/vistas';
import type { Producto, RespuestaTurnoActual } from './tipos/contrato';

export default function App() {
  const sesion = useSesion();
  const ahora = useAhora();

  const mesas = useMesas(ahora);
  const catalogo = useCatalogo();
  const cobros = useCobros() ?? [];
  const pendientes = usePendientesSync() ?? 0;
  const pedido = usePedido(sesion.ordenSeleccionada);

  const [bajando, setBajando] = useState(false);

  /**
   * Estado de la caja. `undefined` = todavía no se sabe (arrancando);
   * `{ turno: null }` = confirmado que no hay turno abierto. La diferencia
   * importa: no es lo mismo "no sé" que "la caja está cerrada", y cada caso
   * pinta una pantalla distinta.
   */
  const [turno, setTurno] = useState<RespuestaTurnoActual | undefined>(undefined);
  const [cerrandoCaja, setCerrandoCaja] = useState(false);
  /** Puesto cuyas cuentas se están mostrando para elegir. */
  const [eligiendoCuenta, setEligiendoCuenta] = useState<string | null>(null);
  const [creandoPuesto, setCreandoPuesto] = useState(false);
  const [editandoCarta, setEditandoCarta] = useState(false);

  /* ── Sincronización ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!sesion.autenticado) return;

    // Pull de arranque. Si falla —tablet sin señal al abrir— no pasa nada:
    // se trabaja con la copia local que quedó de la última vez.
    setBajando(true);
    void bajarTodo()
        .then(async () => setTurno((await turnoGuardado()) ?? { turno: null }))
        .catch(async () => {
          // Sin señal al arrancar: se trabaja con lo último que quedó guardado,
          // incluido el estado de la caja.
          setTurno((await turnoGuardado()) ?? { turno: null });
          sesion.avisar('Sin conexión: trabajando con la copia local');
        })
        .finally(() => setBajando(false));

    return iniciarSync((r) => sesion.setConexion(navigator.onLine, r.pendientes));
    // Solo debe re-ejecutarse al iniciar o cerrar sesión.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesion.autenticado]);

  useEffect(() => {
    const cambio = () => sesion.setConexion(navigator.onLine);
    window.addEventListener('online', cambio);
    window.addEventListener('offline', cambio);
    return () => {
      window.removeEventListener('online', cambio);
      window.removeEventListener('offline', cambio);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Derivados ────────────────────────────────────────────────────────── */

  const nombresProducto = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of catalogo?.productos ?? []) m.set(p.id, p.nombre);
    return m;
  }, [catalogo]);

  const iconoDe = useCallback(
      (productoId: string) => iconoPorNombre(nombresProducto.get(productoId) ?? ''),
      [nombresProducto],
  );

  const mesaActual = useMemo(
      () => (mesas ?? []).find((m) => m.id === sesion.mesaSeleccionada) ?? null,
      [mesas, sesion.mesaSeleccionada],
  );
  /**
   * Totales de caja calculados desde IndexedDB.
   *
   * El servidor no sirve acá: `repo.cobrar` escribe local y encola, así que el
   * POST ocurre después. Pedirle el resumen al backend justo tras cobrar
   * devolvía el total sin esa venta — siempre una atrás.
   *
   * Va antes de los `return` condicionales: los hooks deben ejecutarse en el
   * mismo orden en cada render, y con la caja cerrada este devuelve undefined.
   */
  const resumenLocal = useResumenTurno(
      turno?.turno?.id ?? null,
      Number(turno?.turno?.baseInicial ?? 0),
  );

  /* ── Acciones ─────────────────────────────────────────────────────────── */

  const datosCuenta = (comensales: number, etiqueta?: string | null) => ({
    comensales,
    mesero: sesion.usuario?.nombre ?? '—',
    dispositivoId: dispositivoId(),
    etiqueta: etiqueta ?? null,
  });

  /** Abre una cuenta y salta al catálogo: nadie abre una mesa para dejarla vacía. */
  const abrirCuentaEn = async (mesa: MesaVista, etiqueta?: string | null) => {
    const orden = await repo.abrirCuenta(
        mesa.id,
        mesa.numero,
        datosCuenta(mesa.capacidad, etiqueta),
    );
    sesion.seleccionar(mesa.id, orden.id);
    sesion.abrirModal('productos');
    sesion.avisar(
        mesa.cuentas.length > 0
            ? `Segunda cuenta abierta en ${mesa.nombre ?? `mesa ${pad2(mesa.numero)}`}`
            : `${mesa.nombre ?? `Mesa ${pad2(mesa.numero)}`} abierta · pedido #${orden.numero}`,
    );
  };

  const seleccionarMesa = async (mesa: MesaVista) => {
    // Con una sola cuenta se entra directo: es el 90% de los casos y un paso
    // intermedio ahí sería fricción pura. Con varias, hay que preguntar cuál.
    if (mesa.cuentas.length === 1) {
      const unica = mesa.cuentas[0];
      if (unica) sesion.seleccionar(mesa.id, unica.orden.id);
      return;
    }

    if (mesa.cuentas.length > 1) {
      setEligiendoCuenta(mesa.id);
      return;
    }

    await abrirCuentaEn(mesa);
  };

  /** Puesto creado al vuelo: la barra, una mesa prestada, un domicilio en sitio. */
  const crearPuesto = async (datos: {
    nombre: string;
    capacidad: number;
    esTemporal: boolean;
    etiqueta: string | null;
  }) => {
    const orden = await repo.abrirPuestoNuevo(
        { nombre: datos.nombre, capacidad: datos.capacidad, esTemporal: datos.esTemporal },
        datosCuenta(datos.capacidad, datos.etiqueta),
    );

    setCreandoPuesto(false);
    sesion.seleccionar(orden.mesaId, orden.id);
    sesion.abrirModal('productos');
    sesion.avisar(`${datos.nombre} abierto · pedido #${orden.numero}`);
  };

  const agregarProducto = (p: Producto) => {
    if (!pedido.orden) return;
    void repo.agregarProducto(pedido.orden.id, p);
  };

  /**
   * jsPDF pesa ~380 kB y solo hace falta al cobrar, así que se carga bajo
   * demanda. La primera pantalla —que es la que espera el mesero— no paga ese
   * costo. El service worker ya lo tiene cacheado para cuando no haya red.
   */
  /**
   * Al cobrar se abre el diálogo de impresión con el ticket en texto plano.
   *
   * No se imprime el PDF: una térmica de 80 mm no lo entiende, y el driver
   * termina extrayéndole el texto y perdiendo el alineado —"Subtotal$ 27.900"
   * pegado— además de romper los acentos. El ticket de texto se imprime como
   * imagen desde el navegador, así que sale igual en cualquier impresora.
   *
   * El PDF sigue disponible desde "Últimos cobros" para mandar por WhatsApp.
   */
  const emitirRecibo = async (pago: PagoLocal) => {
    const [{ emisorDesde }, { imprimirTicket }] = await Promise.all([
      import('./recibo/pdf'),
      import('./recibo/texto'),
    ]);
    imprimirTicket(pago, emisorDesde(sesion.restaurante));
  };

  const descargarPdf = async (pago: PagoLocal) => {
    const { descargarRecibo, emisorDesde } = await import('./recibo/pdf');
    // Los datos del negocio salen del login, no del código: así el mismo build
    // sirve para el segundo restaurante sin tocar nada.
    descargarRecibo(pago, emisorDesde(sesion.restaurante));
  };

  const cobrar = async (datos: repo.DatosCobro) => {
    if (!pedido.orden) return;
    const pago = await repo.cobrar(pedido.orden.id, datos);

    await emitirRecibo(pago);

    sesion.abrirModal(null);
    sesion.seleccionar(null, null);
    sesion.avisar(`Mesa ${pad2(pago.mesaNumero ?? '—')} cobrada · imprimiendo recibo`);
    // El resumen se recalcula solo: useResumenTurno lee IndexedDB con useLiveQuery.
  };

  /** Deshace una cuenta abierta por error. Solo aplica si está vacía. */
  const descartarCuenta = async () => {
    const orden = pedido.orden;
    if (!orden) return;

    const ok = await repo.descartarCuenta(orden.id);
    if (!ok) {
      sesion.avisar('La cuenta ya tiene productos: anulala desde el pedido.');
      return;
    }

    sesion.seleccionar(null, null);
    sesion.avisar(`Mesa ${pad2(orden.mesaNumero ?? '—')} liberada`);
  };

  const reimprimir = (pago: PagoLocal) => {
    void descargarPdf(pago).then(() => sesion.avisar(`Recibo #${pago.ordenNumero} descargado`));
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  if (!sesion.autenticado) {
    return (
        <Login
            onEntrar={(r) => {
              sesion.iniciarSesion(r.usuario, r.restaurante, r.token);
            }}
        />
    );
  }

  if (turno === undefined) {
    return <div className="cargando">Cargando…</div>;
  }

  if (turno.turno === null) {
    return (
        <AperturaCaja
            nombreRestaurante={sesion.restaurante?.nombre ?? 'Lordaen POS'}
            cajero={sesion.usuario?.nombre ?? ''}
            onCerrarSesion={sesion.cerrarSesion}
            onAbierto={(t) => {
              setTurno({ turno: t, puedeCerrar: true });
              void bajarTurno().then(setTurno).catch(() => {});
              sesion.avisar(`Caja abierta · turno #${t.numero}`);
            }}
        />
    );
  }

  return (
      <>
        <div className="app">
          <Rail onCerrarSesion={sesion.cerrarSesion} onCarta={() => setEditandoCarta(true)} />

          <div className="stage">
            <div className="col">
              <PanelMesas
                  mesas={bajando && mesas?.length === 0 ? undefined : mesas}
                  seleccionada={sesion.mesaSeleccionada}
                  filtro={sesion.filtroMesas}
                  onFiltro={sesion.setFiltro}
                  onSeleccionar={(m) => void seleccionarMesa(m)}
                  onNuevoPuesto={() => setCreandoPuesto(true)}
              />
            </div>

            <PanelOrden
                pedido={pedido}
                iconoDe={iconoDe}
                onAgregar={() => sesion.abrirModal('productos')}
                onEditarDatos={() => sesion.abrirModal('datosMesa')}
                onCobrar={() => sesion.abrirModal('pago')}
                onDescartar={() => void descartarCuenta()}
                onPedirCuenta={() => {
                  if (!pedido.orden) return;
                  void repo.pedirCuenta(pedido.orden.id);
                  sesion.avisar(`Mesa ${pad2(pedido.orden.mesaNumero ?? '—')} marcada como cuenta pedida`);
                }}
                onPropina={(aceptada) => {
                  if (!pedido.orden) return;
                  void repo.marcarPropina(pedido.orden.id, aceptada);
                }}
            />
            <div className="col side">
              <PanelLateral
                  usuario={sesion.usuario}
                  mesaActual={mesaActual}
                  minutosCuenta={
                    pedido.orden ? Math.floor((ahora - pedido.orden.abiertaEn) / 60000) : null
                  }
                  mesas={mesas ?? []}
                  cobros={cobros}
                  enLinea={sesion.enLinea}
                  pendientes={pendientes}
                  turno={{ ...turno, resumen: resumenLocal ?? turno.resumen }}
                  onDescargarRecibo={reimprimir}
                  onCerrarCaja={() => {
                    if (pendientes > 0) {
                      sesion.avisar(`Hay ${pendientes} operación(es) sin sincronizar. Esperá un momento.`);
                      return;
                    }
                    void bajarTurno().then(setTurno).catch(() => {});
                    setCerrandoCaja(true);
                  }}
              />
            </div>
          </div>
        </div>

        {sesion.modal === 'productos' && pedido.orden && (
            <ModalProductos
                catalogo={catalogo}
                orden={pedido.orden}
                items={pedido.items}
                subtotal={pedido.totales.subtotal}
                iconoDe={iconoDe}
                onAgregar={agregarProducto}
                onCerrar={() => sesion.abrirModal(null)}
            />
        )}

        {sesion.modal === 'pago' && pedido.orden && (
            <ModalPago
                total={pedido.totales.total}
                onCerrar={() => sesion.abrirModal(null)}
                onConfirmar={(d) => void cobrar(d)}
            />
        )}

        {sesion.modal === 'datosMesa' && pedido.orden && (
            <ModalDatosMesa
                comensales={pedido.orden.comensales}
                mesero={pedido.orden.meseroNombre}
                etiqueta={pedido.orden.etiqueta}
                mesaActualId={pedido.orden.mesaId}
                mesasLibres={(mesas ?? []).filter((m) => m.estado === 'libre')}
                onCerrar={() => sesion.abrirModal(null)}
                onGuardar={(d) => {
                  const orden = pedido.orden;
                  if (!orden) return;

                  void repo.renombrarCuenta(orden.id, {
                    etiqueta: d.etiqueta,
                    comensales: d.comensales,
                  });
                  void repo.asignarMesero(orden.id, d.mesero);

                  if (d.moverA) {
                    void repo.moverCuenta(orden.id, { id: d.moverA.id, numero: d.moverA.numero });
                    sesion.seleccionar(d.moverA.id, orden.id);
                    sesion.avisar(
                        `Cuenta movida a ${d.moverA.nombre ?? `mesa ${pad2(d.moverA.numero)}`}`,
                    );
                  }

                  sesion.abrirModal(null);
                }}
            />
        )}

        {eligiendoCuenta &&
            (() => {
              const mesa = (mesas ?? []).find((m) => m.id === eligiendoCuenta);
              if (!mesa) return null;
              return (
                  <ModalCuentas
                      mesa={mesa}
                      onCerrar={() => setEligiendoCuenta(null)}
                      onElegir={(ordenId) => {
                        sesion.seleccionar(mesa.id, ordenId);
                        setEligiendoCuenta(null);
                      }}
                      onNuevaCuenta={() => {
                        setEligiendoCuenta(null);
                        void abrirCuentaEn(mesa, `Cuenta ${mesa.cuentas.length + 1}`);
                      }}
                  />
              );
            })()}

        {editandoCarta && (
            <ModalCatalogo
                catalogo={catalogo}
                onCerrar={() => setEditandoCarta(false)}
                onAviso={sesion.avisar}
            />
        )}

        {creandoPuesto && (
            <ModalPuestoNuevo
                onCerrar={() => setCreandoPuesto(false)}
                onCrear={(d) => void crearPuesto(d)}
            />
        )}

        {cerrandoCaja && (
            <ModalCierreCaja
                actual={turno}
                onCerrar={() => setCerrandoCaja(false)}
                onCerrado={(arqueo) => {
                  setCerrandoCaja(false);
                  setTurno({ turno: null });
                  sesion.seleccionar(null, null);
                  sesion.avisar(
                      arqueo.estado === 'cuadrado'
                          ? 'Caja cerrada · cuadró exacta'
                          : `Caja cerrada · ${arqueo.estado} de ${arqueo.diferencia}`,
                  );
                }}
            />
        )}

        <Aviso texto={sesion.aviso} onOcultar={() => sesion.avisar(null)} />
      </>
  );
}